import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, RefreshCw } from "lucide-react";
import { apiGet } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ModulePage, Empty, BTN_GHOST, fmtTime } from "@/components/common/ModuleShell";

const KIND_COLOR = { task: "text-jarvis-cyanBright", session: "text-jarvis-purple", vault: "text-jarvis-amber", servers: "text-jarvis-teal", models: "text-jarvis-blue", ai: "text-jarvis-violet" };
const LEVEL_DOT = { info: "bg-jarvis-cyan/70", warning: "bg-jarvis-amber", error: "bg-jarvis-red" };

function EventRow({ e }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b border-white/5 last:border-0" data-testid={`activity-event-${e.kind}`}>
      <span className={cn("mt-1.5 w-1.5 h-1.5 rounded-full shrink-0", LEVEL_DOT[e.level] || LEVEL_DOT.info)} />
      <span className="font-num text-[11px] text-slate-500 w-32 shrink-0">{fmtTime(e.ts)}</span>
      <span className={cn("font-mono text-[10px] uppercase tracking-wider w-16 shrink-0", KIND_COLOR[e.kind] || "text-slate-400")}>{e.kind}</span>
      <span className="text-sm text-slate-200 leading-snug">{e.text}</span>
    </div>
  );
}

export default function ActivityPage() {
  const [data, setData] = useState(null), [kind, setKind] = useState(null), [busy, setBusy] = useState(false);
  const load = useCallback(() => { setBusy(true); apiGet(`/activity/feed?limit=100${kind ? `&kind=${kind}` : ""}`).then(setData).catch((e) => toast.error(e.message)).finally(() => setBusy(false)); }, [kind]);
  useEffect(() => { load(); const id = setInterval(load, 10000); return () => clearInterval(id); }, [load]);
  const kinds = data?.kinds || [];
  return (
    <ModulePage icon={Activity} title="Activity" testId="activity-page"
      subtitle={data ? `${data.events.length} recorded events · worker jobs, owner sessions, vault access, server probes · no synthetic entries` : "Loading…"}
      right={<button onClick={load} disabled={busy} className={BTN_GHOST} data-testid="activity-refresh"><RefreshCw className={cn("w-3.5 h-3.5", busy && "animate-spin")} />Refresh</button>}>
      <div className="flex flex-wrap gap-2 mb-4">
        {[null, ...kinds].map((k) => <button key={k || "all"} onClick={() => setKind(k)} data-testid={`activity-filter-${k || "all"}`}
          className={cn("font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-md border transition-colors", kind === k ? "border-jarvis-cyan/50 text-jarvis-cyanBright bg-jarvis-cyan/10" : "border-white/10 text-slate-500 hover:text-slate-300")}>{k || "all"}</button>)}
      </div>
      <div className="jv-panel p-4" data-testid="activity-list">
        {data?.events.length === 0 && <Empty testId="activity-empty">No events recorded for this filter.</Empty>}
        {data?.events.map((e) => <EventRow key={`${e.ts}-${e.kind}-${e.text}`} e={e} />)}
      </div>
    </ModulePage>
  );
}
